import type { GrantablePermission } from '@palaccount/contracts';
import { Loader2 } from 'lucide-react';
import React, { type ReactNode } from 'react';
import { Link } from 'react-router';

import { DocumentHeaderControls, type DocumentHeaderSpec } from '@/components/document-header-controls';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';

/**
 * THE DOCUMENT TOOLBAR — one place where a document editor's action buttons
 * are DECLARED rather than hand-written, so that PART 4.6's button table can
 * one day be checked against them.
 *
 * ── WHY THIS EXISTS, AND WHAT ITS COUNTDOWN EARNED ────────────────────────
 *
 * GEN-55, spec line 3799, verbatim:
 *
 *   "A button named in this table must do exactly what this table says, on
 *    every screen, with no exception."
 *
 * That is a claim quantified over EVERY button on EVERY screen, and a
 * citation is a promise that somebody could check it. When this primitive was
 * born the toolbars were seventeen hand-copied blocks of nested JSX ternaries
 * — no set of buttons for a test to quantify over — so GEN-55 was
 * deliberately cited nowhere in this file, and
 * `src/test/document-toolbar-roster.test.tsx` was the countdown that said
 * when the citation would become available. THAT MOMENT CAME under delegated
 * ruling D51 (27 Aug 2026): the roster ran 16 → 0, the last two editors
 * (FIN-02's journal, FIN-06's cash transfer) joined under the ruling's two
 * layout calls, and the citation now lives at the `row` field below — the
 * declaration that makes the sentence checkable — SCOPED to what the empty
 * roster actually earned: every DOCUMENT EDITOR's buttons are declared. The
 * table's New and Excel live on list screens, and Search, the
 * record-navigation arrows and Menu are unbuilt; the roster's header keeps
 * that boundary in its own words.
 *
 * ── WHAT THE DECLARATION CARRIES, AND WHY EACH FIELD IS THERE ─────────────
 *
 * `row` is the load-bearing one and it is the reason this is a declaration
 * rather than a tidier set of components. It names WHICH ROW of PART 4.6's
 * table an act is, or `null` for an act the table does not name (a purchase
 * order's Send, an invoice's "Credit this invoice"). A test that wants to ask
 * "does every Delete on every migrated screen do what the table says Delete
 * does" needs the buttons to have said which one they are; nothing in the
 * hand-written JSX ever did. This is GEN-55's enforcement seam for document
 * editors — the declaration is what turns "on every screen, with no
 * exception" from prose into a set a test can quantify over, and the roster's
 * test (3) is what keeps that set equal to ALL of them.
 *
 * ── HIDDEN VERSUS GREYED: TWO DIFFERENT QUESTIONS, TWO DIFFERENT FIELDS ───
 *
 * This is the distinction the primitive exists to stop screens conflating,
 * and the specification contradicts itself about it, so both halves are
 * written down here.
 *
 * GEN-56, spec line 3801: "A button the user's role forbids must be hidden,
 * not greyed out. A greyed button must mean 'not now'; a missing button must
 * mean 'not you'."
 *
 * PART 16.7 row 2 requires the REVERSE for the permission half, in the same
 * words it forbids it — "Show the screen and GREY OUT THE BUTTON… THE BUTTON
 * MUST NEVER VANISH, or the person cannot tell whether the feature exists"
 * (BR-PERM-17, BR-PERM-18). `permission-gate.tsx` records at length why 16.7
 * governs: it is the permission model's own chapter and the later writing,
 * and GEN-56's stated worry — a support call — is answered by the tooltip
 * `Gated` requires and GEN-56 assumed would not be there.
 *
 * So the two fields are NOT interchangeable and a screen may not swap one for
 * the other:
 *
 *   `visible` — the DOCUMENT's question. A posted invoice has no Delete for
 *               anybody, the Owner included (BR-PERM-02, GEN-70). False here
 *               means the act renders NOTHING — no greyed ghost, because a
 *               greyed one would claim a role could get it back.
 *   `gate`    — the ROLE's question. Renders through `Gated`: the same
 *               button, inert, at 50%, carrying the reason on hover, on focus
 *               and to a screen reader. Never `null`.
 *   `disabled`— GEN-56's "not now" proper, and 16.7 row 3's rule-or-limit: an
 *               unbalanced journal, a variance not yet accepted, a save
 *               already in flight.
 *
 * `busy` is GEN-60, spec line 3807: "A button that starts a slow job must
 * grey itself out and show a spinner until the job finishes, so it cannot be
 * clicked twice." The spinner and the disabling travel together here so a
 * screen cannot ship one without the other.
 *
 * ── THE DIALOGS STAY WITH THE SCREEN, ON PURPOSE ──────────────────────────
 *
 * An action that asks first — GEN-209's list, spec line 4709: "deleting a
 * draft, cancelling a confirmed document, switching a master record off,
 * revoking an API key, approving or rejecting a submission, posting a
 * revaluation, and resetting a layout" — sets its own `open` state from
 * `onAct` and renders `DeleteConfirmBox` / `ConfirmBox` / `ReasonBox` in its
 * OWN file, as a sibling of this toolbar. This component hosts that pattern
 * and does not absorb it, for three reasons, all of them checked:
 *
 *   1. `confirm-box.tsx` has no imperative API — no `askConfirm`, no context.
 *      The caller owns the boolean. Absorbing the dialog would mean inventing
 *      that API, which GEN-204's "a screen must not build its own" and §16.5.3
 *      both argue against.
 *   2. `gen58-delete-confirmation.test.tsx` counts `<DeleteConfirmBox` render
 *      sites across `apps/web/src/features/**` and requires every one of them
 *      to be named in its pinned inventory. A dialog moved into
 *      `components/` would leave that count, and GEN-58's second clause —
 *      "must never appear on a confirmed record" — would lose the guard that
 *      is currently the only thing checking it.
 *   3. That same test pins each screen's delete VISIBILITY GUARD as literal
 *      source text. Keeping the dialog under its own ternary keeps the guard
 *      where the test reads it, and keeps the property the screens' own
 *      comments claim: the dialog cannot outlive the condition that allows
 *      the act.
 *
 * ── WHAT IS NOT A TOOLBAR ACT, DECIDED ONCE SO IT IS NOT RE-ARGUED ────────
 *
 * The twins' migration (FIN-03's receipt voucher, FIN-04's payment voucher)
 * was handed one open question by the roster: the payment's approval trio —
 * Pending / Approved / Rejected, three raw `<button>` elements carrying
 * `aria-pressed`, each in its own `Gated`. THE ANSWER IS THAT IT IS NOT A
 * TOOLBAR ACT AND IT DID NOT MOVE, on four grounds, each of which is
 * independently sufficient and none of which is "it looks different":
 *
 *   1. IT IS A SELECTOR, NOT A LIST OF ACTS. The three are mutually exclusive
 *      and exactly one is pressed; what they read is `payment.approvalStatus`,
 *      a value that arrives from the SERVER. A toolbar act asks "do this"; a
 *      segmented control asks "which of these is it". The same screen has two
 *      more of the same class — the supplier/expense type toggle and the
 *      five-way method picker — and nobody would call those toolbar acts.
 *   2. PART 4.6's TABLE HAS NO ROW ANY OF THEM COULD NAME, so all three would
 *      declare `row: null`, and `row` is the whole reason this is a
 *      declaration rather than a tidier set of components. Three acts that the
 *      table cannot check, moved into the structure that exists to make the
 *      table checkable, is motion without gain.
 *   3. IT IS NOT IN THE TOOLBAR ROW. It lives in the approval Card at §5a,
 *      behind `effectiveRequire`, sharing a flex row with the approval-date
 *      chip (BR-FIN-04-04). Moving it would move it ON THE SCREEN, and this
 *      primitive's whole contract is that behaviour stays byte-equal.
 *   4. THE ONLY SHAPE THAT WOULD FIT IS THE `node` ESCAPE, and that escape is
 *      pinned at ONE by `document-toolbar-roster.test.tsx` (6) precisely so a
 *      second hole has to be argued for rather than typed. `aria-pressed`,
 *      three per-state palettes and a three-way toggle are not expressible as
 *      `ButtonAction`, so the alternative to leaving it alone is growing the
 *      declaration a control shape — which is a change to what the primitive
 *      IS, and not a refactor's to make.
 *
 * The trio's own GEN-209 asks are untouched and stay where they were:
 * `features/payments/payment-approval-asks.test.tsx` presses all three and
 * reads the decision off the wire. That file is the guard; this paragraph is
 * only the reason it was not disturbed.
 *
 * THIS IS ABOUT A CONTROL CLASS, NOT ABOUT THE WORD "APPROVE". FIN-02's
 * journal editor, still on the roster, has Approve and Reject as BUTTONS THAT
 * ACT — press and it happens — which is a toolbar act wearing a similar name.
 * The verdict here does not read across to it.
 *
 * ── WHAT THE MIGRATION DELIBERATELY DID NOT CHANGE ────────────────────────
 *
 * This primitive was introduced by a refactor whose whole contract was that
 * behaviour stays byte-equal, so it renders what the screens already drew and
 * three GEN-55 deviations survive it. They are recorded rather than fixed, rule-coverage:ignore
 * because each is a product decision and none is this refactor's to take:
 *
 *   - The table draws Delete "White, outlined". Every editor draws it
 *     `destructive` (red). Sixteen screens agree with each other and disagree
 *     with the table.
 *   - The table's **Cancel** "marks a confirmed record as no longer valid, and
 *     undoes its accounting", in red. The button labelled `common.cancel` at
 *     the head of every editor toolbar is a LINK BACK TO THE LIST — a
 *     different act entirely, sharing the name. The act the table means is
 *     spelled `bills.cancelPosted`, `po.cancelOrder`, `receipts.cancelDraft`.
 *     One label, two meanings, is exactly what GEN-55 forbids, and no screen rule-coverage:ignore
 *     can fix it alone.
 *   - The table puts **Confirm** at "the trailing edge of the status strip".
 *     Every editor puts Post in the totals card instead.
 *
 * The table's ORDER — New first, Save second, Edit third, Delete fourth,
 * Print fifth, Excel sixth — is likewise not imposed. It is now at least
 * VISIBLE: an array literal a reader can see in one glance, where before it
 * was the nesting order of seven ternaries. Aligning it is the next batch's
 * decision to take with the owner, not a refactor's to take quietly.
 */

/**
 * The rows PART 4.6's table actually has. An act that is not one of them
 * declares `row: null` — which is a statement, not an omission: it says the
 * table does not govern this button, so GEN-55 has nothing to say about it. rule-coverage:ignore
 */
export type ToolbarTableRow =
  | 'new'
  | 'save'
  | 'edit'
  | 'delete'
  | 'print'
  | 'excel'
  | 'search'
  | 'navigate'
  | 'menu'
  | 'cancel'
  | 'confirm'
  | 'addLine';

type ActionCommon = {
  /** Stable within a screen, and the key React renders this act under. */
  id: string;
  /** Which row of PART 4.6's table this is, or `null` if the table has none. */
  row: ToolbarTableRow | null;
};

/**
 * THE ONE ESCAPE, AND WHY IT IS NAMED RATHER THAN QUIETLY ALLOWED.
 *
 * A `node` act is a position in the toolbar holding a component that is not
 * one button and cannot be described as one: it owns state of its own, or it
 * draws several controls that belong together. Today there is exactly ONE —
 * `DocumentActions`, the Print/PDF pair, whose PDF button carries its own
 * idle/working/failed state and its own failure line.
 *
 * It is a real hole in the declaration: a `node` act's buttons are invisible
 * to anything that reads the array, so PART 4.6's Print and Excel rows cannot
 * be checked through it. That is why it is a separate arm of the union with
 * its own field name — one grep finds every use, and
 * `document-toolbar-roster.test.tsx` pins the count so it may shrink and not
 * grow. Closing it means teaching `DocumentActions` to be two declared acts,
 * which is a change to what Print and PDF DO and therefore not a refactor's
 * to make.
 *
 * A `node` carries its own visibility inside the expression the screen passes
 * — `cond ? <X/> : null` — rather than through `visible`, because unlike a
 * button's props the node is built eagerly and a screen's `invoice.id` cannot
 * be read before the invoice arrives.
 */
type NodeAction = ActionCommon & { node: ReactNode };

type ButtonAction = ActionCommon & {
  /**
   * ALREADY TRANSLATED, exactly as `DocumentNumberChip` takes its label. The
   * `t('…')` call stays in the screen's own source, which is where
   * `i18n-key-existence.test.ts` reads keys from and where a reviewer looks
   * for them.
   */
  label: string;
  /** The leading icon, or nothing. Rendered `aria-hidden` by its caller. */
  icon?: ReactNode;
  /** The Button variant the screen already drew. See the deviations above. */
  variant?: 'default' | 'outline' | 'destructive';
  /** Extra classes for this one button, where a screen already had them. */
  className?: string;
  /** The Button size the screen already drew. */
  size?: 'default' | 'sm';
  /**
   * The DOCUMENT's question (BR-PERM-02, GEN-70). Defaults to `true`. False
   * renders nothing at all — never a greyed ghost.
   */
  visible?: boolean;
  /**
   * The ROLE's question (16.7 row 2, BR-PERM-17/-18). Greys with the reason;
   * never hides. `reasonKey` holds the VERB PHRASE only — "delete bills" —
   * and `perm.roleCannot` supplies the sentence around it, once.
   */
  gate?: { permission: GrantablePermission; reasonKey: string };
  /** GEN-56's "not now", and 16.7 row 3's rule or limit. */
  disabled?: boolean;
  /** GEN-60: the spinner and the disabling, together or not at all. */
  busy?: boolean;
} & (
  | {
      /**
       * What the act does. For an act on GEN-209's list this opens the
       * screen's own dialog and does nothing else — GEN-208, "nothing must
       * happen until the action button is clicked".
       */
      onAct: () => void;
      to?: undefined;
    }
  | {
      /** A navigation. Rendered `asChild` over a real `<Link>` (NFR-36). */
      to: string;
      onAct?: undefined;
    }
);

export type DocumentToolbarAction = ButtonAction | NodeAction;

/**
 * The trailing action group of a document editor's toolbar row.
 *
 * Renders the `ms-auto flex flex-wrap gap-2` box the seventeen editors each
 * wrote by hand, so the box itself stops being copied along with its contents.
 * `children` is the slot the screen's own dialogs sit in, unchanged and in
 * their existing place in the DOM.
 */
export function DocumentToolbar({
  actions,
  header,
  children,
}: {
  actions: ReadonlyArray<DocumentToolbarAction>;
  /**
   * S4 — the record-navigation rail (‹‹ ››, Go to, Find, New) that PART 4.6's
   * Search and navigation rows name and this header once reported as unbuilt.
   * Declared by kind rather than drawn per screen — see
   * `document-header-controls.tsx` — so every editor gets the same rail.
   */
  header?: DocumentHeaderSpec;
  /** The screen's confirm dialogs — see "the dialogs stay with the screen". */
  children?: ReactNode;
}) {
  return (
    <div className="ms-auto flex flex-wrap gap-2">
      {header ? <DocumentHeaderControls {...header} /> : null}
      {actions.map((action) =>
        'node' in action ? (
          <React.Fragment key={action.id}>{action.node}</React.Fragment>
        ) : action.visible === false ? null : (
          <ToolbarButton key={action.id} action={action} />
        ),
      )}
      {children}
    </div>
  );
}

/**
 * One act, drawn.
 *
 * `Gated` wraps exactly ONE interactive child and clones it, so the wrapping
 * happens here rather than around the group: two acts with two different
 * permissions cannot share a gate, and an ungated act must not be greyed by
 * its neighbour's.
 */
function ToolbarButton({ action }: { action: ButtonAction }) {
  const button = (
    <Button
      variant={action.variant ?? 'default'}
      size={action.size ?? 'default'}
      className={action.className}
      {...(action.to === undefined
        ? { onClick: action.onAct, disabled: action.disabled === true || action.busy === true }
        : { asChild: true as const })}
    >
      {action.to === undefined ? (
        <>
          {/* GEN-60: while the job runs the spinner REPLACES the icon, which
              is what every migrated screen already did — two glyphs would
              widen the button mid-click. */}
          {action.busy === true ? <Loader2 className="animate-spin" aria-hidden /> : action.icon}
          {action.label}
        </>
      ) : (
        <Link to={action.to}>
          {action.icon}
          {action.label}
        </Link>
      )}
    </Button>
  );

  if (action.gate === undefined) return button;
  return (
    <Gated permission={action.gate.permission} reasonKey={action.gate.reasonKey}>
      {button}
    </Gated>
  );
}
